import { Injectable } from "@angular/core";
import { isEmpty } from "lodash";
import { Observable, Subject } from "rxjs";
import { Roles, RouteType } from "src/app/models/enum";
import { User } from "src/app/models/models";
import { SESSION } from "src/session.config";
import { isInstructorViewRole, isPilotViewRole } from "../utils/user-role-view.utils";

@Injectable({
	providedIn: "root",
})
export class RoleService {
	private authenticatedUser$ = new Subject<User>();
	private user!: User;
	private _isAdmin: boolean = false;
	private _isPilot: boolean = false;
	private _isInstructor: boolean = false;
	private _isOfficeAdmin: boolean = false;
	private _isGroupAdmin: boolean = false;
	private _isCabinCrew: boolean = false;
	private _isCabinCrewInstructor: boolean = false;

	constructor() {
		this.setInitRole();
	}

	setAuthenticatedUser(user: User) {
		this.authenticatedUser$.next(user);
	}

	getAuthenticatedUser(): Observable<any> {
		return this.authenticatedUser$.asObservable();
	}

	get userDetails() {
		return this.user || JSON.parse(localStorage.getItem(SESSION.SESSION_KEY_USER) as string);
	}

	set userDetails(user: User) {
		this.user = user;
		localStorage.setItem(SESSION.SESSION_KEY_USER, JSON.stringify(user));
	}

	getUserRole(): string {
		const selectedRole = this.userDetails?.selectedRole;
		if (selectedRole) {
			return selectedRole;
		}

		const roles = this.userDetails?.roles ?? [];
		if (roles.includes(Roles.ADMIN)) return Roles.ADMIN;
		if (roles.includes(Roles.INSTRUCTOR)) return Roles.INSTRUCTOR;
		if (roles.includes(Roles.CABIN_CREW_INSTRUCTOR)) return Roles.CABIN_CREW_INSTRUCTOR;
		if (roles.includes(Roles.OFFICE_ADMIN)) return Roles.OFFICE_ADMIN;
		if (roles.includes(Roles.GROUP_ADMIN)) return Roles.GROUP_ADMIN;
		if (roles.includes(Roles.CABIN_CREW)) return Roles.CABIN_CREW;
		if (roles.includes(Roles.PILOT)) return Roles.PILOT;

		return Roles.PILOT;
	}

	getUserAOC(): string {
		return this.userDetails.aoc && this.userDetails.aoc.code;
	}

	get isAdmin() {
		return this._isAdmin;
	}

	get isPilot() {
		return this._isPilot;
	}

	get isInstructor() {
		return this._isInstructor;
	}

	get isOfficeAdmin() {
		return this._isOfficeAdmin;
	}

	get isGroupAdmin() {
		return this._isGroupAdmin;
	}

	get isCabinCrew() {
		return this._isCabinCrew;
	}

	get isCabinCrewInstructor() {
		return this._isCabinCrewInstructor;
	}

	get hasPilotView() {
		return isPilotViewRole(this.userDetails?.selectedRole ?? this.getUserRole());
	}

	get hasInstructorView() {
		return isInstructorViewRole(this.userDetails?.selectedRole ?? this.getUserRole());
	}

	get isMultiRole() {
		return this.userDetails?.roles?.length > 1;
	}

	setRolesBool() {
		const role = this.userDetails?.selectedRole ?? this.getUserRole();
		if (isEmpty(this.userDetails?.roles)) {
			this._isAdmin = false;
			this._isPilot = true;
			this._isOfficeAdmin = false;
			this._isInstructor = false;
			this._isGroupAdmin = false;
			this._isCabinCrew = false;
			this._isCabinCrewInstructor = false;
		} else {
			this._isAdmin = role === Roles.ADMIN;
			this._isPilot = role === Roles.PILOT;
			this._isInstructor = role === Roles.INSTRUCTOR;
			this._isOfficeAdmin = role === Roles.OFFICE_ADMIN;
			this._isGroupAdmin = role === Roles.GROUP_ADMIN;
			this._isCabinCrew = role === Roles.CABIN_CREW;
			this._isCabinCrewInstructor = role === Roles.CABIN_CREW_INSTRUCTOR;
		}
	}

	async setInitRole() {
		this._isAdmin = false;
		this._isPilot = false;
		this._isInstructor = false;
		this._isOfficeAdmin = false;
		this._isGroupAdmin = false;
		this._isCabinCrew = false;
		this._isCabinCrewInstructor = false;

		const roles = this.userDetails?.selectedRole ? [this.userDetails?.selectedRole] : this.userDetails?.roles;

		if (roles?.includes(Roles.ADMIN)) {
			this._isAdmin = true;
			return;
		}

		if (roles?.includes(Roles.INSTRUCTOR)) {
			this._isInstructor = true;
			return;
		}

		if (roles?.includes(Roles.CABIN_CREW_INSTRUCTOR)) {
			this._isCabinCrewInstructor = true;
			return;
		}

		if (roles?.includes(Roles.OFFICE_ADMIN)) {
			this._isOfficeAdmin = true;
			return;
		}

		if (roles?.includes(Roles.GROUP_ADMIN)) {
			this._isGroupAdmin = true;
			return;
		}

		if (roles?.includes(Roles.CABIN_CREW)) {
			this._isCabinCrew = true;
			return;
		}

		if (roles?.includes(Roles.PILOT)) {
			this._isPilot = true;
			return;
		}
	}

	getDefaultRoute(role: string = this.getUserRole()): RouteType {
		if (role === Roles.ADMIN || role === Roles.GROUP_ADMIN) {
			return RouteType.FORMS;
		}
		if (isInstructorViewRole(role)) {
			return RouteType.RATINGS;
		}
		return RouteType.REPORT;
	}

	switchRole(role: string) {
		const user = { ...this.userDetails, selectedRole: role };
		this.userDetails = user;
		localStorage.setItem(SESSION.SESSION_KEY_USER_ROLE, role);
		this.setRolesBool();
		setTimeout(() => {
			window.location.href = `/${this.getDefaultRoute(role)}`;
		});
	}

	autoSwitchRole(role: string) {
		let user = this.userDetails;
		user.selectedRole = role;
		localStorage.setItem(SESSION.SESSION_KEY_USER, JSON.stringify(user));
		localStorage.setItem(SESSION.SESSION_KEY_USER_ROLE, role);
	}

	get userHasInstructorRole() {
		const roles = this.userDetails?.roles;
		return roles.includes(Roles.INSTRUCTOR) || roles.includes(Roles.CABIN_CREW_INSTRUCTOR);
	}
}
